import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { useTenant } from '@shared-ui/tenant';
import LandingHub from './Hub';

// The older variants are kept for comparison (`?v=`), so only those who ask
// for one download it.
const LandingFull = lazy(() => import('./Full'));
const LandingMinimal = lazy(() => import('./Minimal'));

/**
 * Active marketing variant. Flip this constant and redeploy to switch live.
 *
 * `hub` is the role split — teacher or student — from
 * docs/FeatureDesigns/front-door/front-door-target.md. The older student-only
 * pages stay reachable for comparison: append `?v=full` or `?v=minimal` to
 * the URL — the query param overrides the constant.
 */
const ACTIVE_VARIANT: VariantKey = 'hub';

const VARIANTS = {
  hub: LandingHub,
  full: LandingFull,
  minimal: LandingMinimal,
} as const;

type VariantKey = keyof typeof VARIANTS;

function isVariantKey(value: string | null): value is VariantKey {
  return value !== null && Object.prototype.hasOwnProperty.call(VARIANTS, value);
}

export default function Landing() {
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const params = new URLSearchParams(search);
  const { tenant, loading } = useTenant();

  // Invite emails link to the app root with `?invite=<email>` (inviteStudent.ts
  // / inviteTenantMember.ts). The root is this marketing page, which would
  // swallow the param and send the invitee to a plain sign-in form via its CTA.
  // Hand the whole query string to /login, which reads `invite` and opens
  // register mode with the address prefilled.
  if (params.has('invite')) {
    return <Navigate to={`/login${search}`} replace />;
  }

  // A tenant URL must not serve generic BuzzBrain marketing — it is a
  // white-label front door, so `/` is the tenant's sign-in. Wait for the lookup
  // rather than flashing the marketing page and then replacing it.
  if (loading) return null;
  if (tenant) return <Navigate to="/login" replace />;

  let variant: VariantKey = ACTIVE_VARIANT;
  const override = params.get('v');
  if (isVariantKey(override)) variant = override;
  const Component = VARIANTS[variant];
  return <Suspense fallback={null}><Component /></Suspense>;
}
